import { normalizeRequiredText } from '@shared/utils/forms/form-value-normalizers.util';
import { ValidationMessageMap } from '@shared/utils/forms/control-error-message.util';

export interface AddressFormValue {
  postalCode: string;
  voivodeship: string;
  district: string;
  commune: string;
  city: string;
  street: string;
  buildingNumber: string;
  apartmentNumber: string;
}

export interface AddressCoordinatesFormValue {
  latitude: number | null;
  longitude: number | null;
}

export type AddressFormValueWithCoordinates = AddressFormValue & AddressCoordinatesFormValue;

export interface AddressSelectOption {
  label: string;
  value: string;
}

export type AddressFieldKey = keyof AddressFormValue;
export const ADDRESS_COORDINATE_FIELD_KEYS = ['latitude', 'longitude'] as const;

export const ADDRESS_POSTAL_CODE_PATTERN = /^\d{2}-\d{3}$/;
export const ADDRESS_REQUIRED_FIELD_MESSAGE = 'To pole jest wymagane.';
export const ADDRESS_POSTAL_CODE_PATTERN_MESSAGE = 'Podaj poprawny kod pocztowy w formacie 00-000.';

export const POLISH_VOIVODESHIPS = [
  'Dolnośląskie',
  'Kujawsko-Pomorskie',
  'Lubelskie',
  'Lubuskie',
  'Łódzkie',
  'Małopolskie',
  'Mazowieckie',
  'Opolskie',
  'Podkarpackie',
  'Podlaskie',
  'Pomorskie',
  'Śląskie',
  'Świętokrzyskie',
  'Warmińsko-Mazurskie',
  'Wielkopolskie',
  'Zachodniopomorskie',
] as const;

export const ADDRESS_VOIVODESHIP_OPTIONS: AddressSelectOption[] = POLISH_VOIVODESHIPS.map(
  (value) => ({
    label: value,
    value,
  })
);

export const ADDRESS_FIELD_VALIDATION_MESSAGES: Record<AddressFieldKey, ValidationMessageMap> = {
  postalCode: {
    required: ADDRESS_REQUIRED_FIELD_MESSAGE,
    pattern: ADDRESS_POSTAL_CODE_PATTERN_MESSAGE,
  },
  voivodeship: {
    required: ADDRESS_REQUIRED_FIELD_MESSAGE,
  },
  district: {
    required: ADDRESS_REQUIRED_FIELD_MESSAGE,
  },
  commune: {
    required: ADDRESS_REQUIRED_FIELD_MESSAGE,
  },
  city: {
    required: ADDRESS_REQUIRED_FIELD_MESSAGE,
  },
  street: {
    required: ADDRESS_REQUIRED_FIELD_MESSAGE,
  },
  buildingNumber: {
    required: ADDRESS_REQUIRED_FIELD_MESSAGE,
  },
  apartmentNumber: {},
};

export function normalizeAddressText(value?: string | null): string {
  return normalizeRequiredText(value);
}

export function normalizeVoivodeshipName(value?: string | null): string {
  const normalizedValue = normalizeAddressText(value);
  if (!normalizedValue) {
    return '';
  }

  const normalizedKey = normalizedValue.toLocaleLowerCase('pl-PL');
  const match = POLISH_VOIVODESHIPS.find(
    (candidate) => candidate.toLocaleLowerCase('pl-PL') === normalizedKey
  );

  return match ?? normalizedValue;
}

export function toAddressFormValue(
  value?: Partial<AddressFormValue> | null
): AddressFormValue {
  return {
    postalCode: normalizeAddressText(value?.postalCode),
    voivodeship: normalizeVoivodeshipName(value?.voivodeship),
    district: normalizeAddressText(value?.district),
    commune: normalizeAddressText(value?.commune),
    city: normalizeAddressText(value?.city),
    street: normalizeAddressText(value?.street),
    buildingNumber: normalizeAddressText(value?.buildingNumber),
    apartmentNumber: normalizeAddressText(value?.apartmentNumber),
  };
}

export function toAddressFormValueWithCoordinates(
  value?: Partial<AddressFormValueWithCoordinates> | null
): AddressFormValueWithCoordinates {
  return {
    ...toAddressFormValue(value),
    latitude: value?.latitude ?? null,
    longitude: value?.longitude ?? null,
  };
}

export function areAddressValuesEqual(
  left?: Partial<AddressFormValue> | null,
  right?: Partial<AddressFormValue> | null
): boolean {
  const leftNormalized = toAddressFormValue(left);
  const rightNormalized = toAddressFormValue(right);

  return (
    leftNormalized.postalCode === rightNormalized.postalCode
    && leftNormalized.voivodeship === rightNormalized.voivodeship
    && leftNormalized.district === rightNormalized.district
    && leftNormalized.commune === rightNormalized.commune
    && leftNormalized.city === rightNormalized.city
    && leftNormalized.street === rightNormalized.street
    && leftNormalized.buildingNumber === rightNormalized.buildingNumber
    && leftNormalized.apartmentNumber === rightNormalized.apartmentNumber
  );
}

export function areAddressValuesWithCoordinatesEqual(
  left?: Partial<AddressFormValueWithCoordinates> | null,
  right?: Partial<AddressFormValueWithCoordinates> | null
): boolean {
  const leftNormalized = toAddressFormValueWithCoordinates(left);
  const rightNormalized = toAddressFormValueWithCoordinates(right);

  return areAddressValuesEqual(leftNormalized, rightNormalized)
    && leftNormalized.latitude === rightNormalized.latitude
    && leftNormalized.longitude === rightNormalized.longitude;
}

export function formatStreetAddressLine(
  value?: Partial<AddressFormValue> | null
): string {
  const normalized = toAddressFormValue(value);
  if (!normalized.street) {
    return '';
  }

  let streetLine = `ul. ${normalized.street}`;

  if (normalized.buildingNumber) {
    streetLine += ` ${normalized.buildingNumber}`;
    if (normalized.apartmentNumber) {
      streetLine += `/${normalized.apartmentNumber}`;
    }
  }

  return streetLine;
}

export function formatPostalCodeCityLine(
  value?: Partial<AddressFormValue> | null
): string {
  const normalized = toAddressFormValue(value);
  return [normalized.postalCode, normalized.city].filter(Boolean).join(' ');
}

export function formatShortAddress(
  value?: Partial<AddressFormValue> | null
): string {
  return [formatStreetAddressLine(value), formatPostalCodeCityLine(value)]
    .filter((part) => part.length > 0)
    .join(', ');
}

export function formatFullAddress(
  value?: Partial<AddressFormValue> | null
): string {
  const normalized = toAddressFormValue(value);
  const administrativeLine = [
    normalized.commune ? `gmina ${normalized.commune}` : '',
    normalized.district ? `powiat ${normalized.district}` : '',
    normalized.voivodeship ? `woj. ${normalized.voivodeship}` : '',
  ]
    .filter((part) => part.length > 0)
    .join(', ');

  return [formatStreetAddressLine(normalized), formatPostalCodeCityLine(normalized), administrativeLine]
    .filter((part) => part.length > 0)
    .join(', ');
}

export function inferVoivodeshipFromPostalCode(postalCode: string): string | null {
  const normalizedPostalCode = normalizeAddressText(postalCode);
  if (!ADDRESS_POSTAL_CODE_PATTERN.test(normalizedPostalCode)) {
    return null;
  }

  const prefix = normalizedPostalCode.substring(0, 2);
  const firstDigit = normalizedPostalCode[0];

  const twoDigitMap: Record<string, string> = {
    '00': 'Mazowieckie',
    '01': 'Mazowieckie',
    '02': 'Mazowieckie',
    '03': 'Mazowieckie',
    '04': 'Mazowieckie',
    '05': 'Mazowieckie',
    '06': 'Mazowieckie',
    '07': 'Mazowieckie',
    '08': 'Mazowieckie',
    '09': 'Mazowieckie',
    '10': 'Warmińsko-Mazurskie',
    '11': 'Warmińsko-Mazurskie',
    '12': 'Warmińsko-Mazurskie',
    '13': 'Warmińsko-Mazurskie',
    '14': 'Warmińsko-Mazurskie',
    '15': 'Podlaskie',
    '16': 'Podlaskie',
    '17': 'Podlaskie',
    '18': 'Podlaskie',
    '19': 'Podlaskie',
    '20': 'Lubelskie',
    '21': 'Lubelskie',
    '22': 'Lubelskie',
    '23': 'Lubelskie',
    '24': 'Lubelskie',
    '25': 'Świętokrzyskie',
    '26': 'Świętokrzyskie',
    '27': 'Świętokrzyskie',
    '28': 'Świętokrzyskie',
    '29': 'Świętokrzyskie',
    '30': 'Małopolskie',
    '31': 'Małopolskie',
    '32': 'Małopolskie',
    '33': 'Małopolskie',
    '34': 'Małopolskie',
    '35': 'Podkarpackie',
    '36': 'Podkarpackie',
    '37': 'Podkarpackie',
    '38': 'Podkarpackie',
    '39': 'Podkarpackie',
    '40': 'Śląskie',
    '41': 'Śląskie',
    '42': 'Śląskie',
    '43': 'Śląskie',
    '44': 'Śląskie',
    '45': 'Opolskie',
    '46': 'Opolskie',
    '47': 'Opolskie',
    '48': 'Opolskie',
    '49': 'Opolskie',
    '50': 'Dolnośląskie',
    '51': 'Dolnośląskie',
    '52': 'Dolnośląskie',
    '53': 'Dolnośląskie',
    '54': 'Dolnośląskie',
    '55': 'Dolnośląskie',
    '56': 'Dolnośląskie',
    '57': 'Dolnośląskie',
    '58': 'Dolnośląskie',
    '59': 'Dolnośląskie',
    '60': 'Wielkopolskie',
    '61': 'Wielkopolskie',
    '62': 'Wielkopolskie',
    '63': 'Wielkopolskie',
    '64': 'Wielkopolskie',
    '65': 'Lubuskie',
    '66': 'Lubuskie',
    '67': 'Lubuskie',
    '68': 'Lubuskie',
    '69': 'Lubuskie',
    '70': 'Zachodniopomorskie',
    '71': 'Zachodniopomorskie',
    '72': 'Zachodniopomorskie',
    '73': 'Zachodniopomorskie',
    '74': 'Zachodniopomorskie',
    '75': 'Zachodniopomorskie',
    '76': 'Zachodniopomorskie',
    '77': 'Pomorskie',
    '78': 'Zachodniopomorskie',
    '80': 'Pomorskie',
    '81': 'Pomorskie',
    '82': 'Pomorskie',
    '83': 'Pomorskie',
    '84': 'Pomorskie',
    '85': 'Kujawsko-Pomorskie',
    '86': 'Kujawsko-Pomorskie',
    '87': 'Kujawsko-Pomorskie',
    '88': 'Kujawsko-Pomorskie',
    '89': 'Kujawsko-Pomorskie',
    '90': 'Łódzkie',
    '91': 'Łódzkie',
    '92': 'Łódzkie',
    '93': 'Łódzkie',
    '94': 'Łódzkie',
    '95': 'Łódzkie',
    '96': 'Mazowieckie',
    '97': 'Łódzkie',
    '98': 'Łódzkie',
    '99': 'Łódzkie',
  };

  if (twoDigitMap[prefix]) {
    return twoDigitMap[prefix];
  }

  const firstDigitMap: Record<string, string> = {
    '0': 'Mazowieckie',
    '1': 'Warmińsko-Mazurskie',
    '2': 'Lubelskie',
    '3': 'Małopolskie',
    '4': 'Śląskie',
    '5': 'Dolnośląskie',
    '6': 'Wielkopolskie',
    '7': 'Zachodniopomorskie',
    '8': 'Pomorskie',
    '9': 'Łódzkie',
  };

  return firstDigitMap[firstDigit] ?? null;
}

export function inferVoivodeshipFromPostalCodeValue(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  return inferVoivodeshipFromPostalCode(value);
}
